/**
 * Renders the right shell for each `useMe()` state so every page
 * (Inbox / Tools / Settings / new pages) doesn't duplicate the loading
 * / unauthorized / no-org branches.
 */

import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useClerk } from '@clerk/react';
import type { MeResponse } from '@/lib/api';
import type { MeStatus } from '@/lib/useMe';
import { CoreIcon } from '@/components/CoreIcon';

export function MeGate({
  status,
  children,
}: {
  status: MeStatus;
  children: (me: MeResponse) => ReactNode;
}) {
  if (status.state === 'pending') return <Pending />;
  if (status.state === 'worker-down') return <WorkerDown status={status} />;
  if (status.state === 'unauthorized') return <Unauthorized status={status} />;
  if (status.state === 'no-org') return <NoOrg />;
  return <>{children(status.me)}</>;
}

// ---------------------------------------------------------------------------

function WorkerDown({ status }: { status: Extract<MeStatus, { state: 'worker-down' }> }) {
  const nav = useNavigate();
  return (
    <div role="alert" className="h-full overflow-y-auto p-8">
      <div className="card p-6 max-w-2xl mx-auto">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-sm bg-destructive/10 border border-destructive/30 grid place-items-center text-destructive shrink-0">
            <CoreIcon name="wifi-signal-off" size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-normal tracking-sub leading-heading mb-1">
              Can&apos;t reach the Worker.
            </h2>
            <p className="text-sm text-foreground/80 leading-body">
              The <code className="font-mono">/api/me</code> request didn&apos;t
              return. Vite proxies API calls to{' '}
              <code className="font-mono">http://localhost:8787</code>.
            </p>
            <div className="mt-4 rounded-sm bg-muted p-4 text-sm leading-body">
              <div className="mono-label mb-2">Start the Worker in a second terminal:</div>
              <pre className="text-[12px] font-mono whitespace-pre-wrap">
{`cd C:\\Users\\FrancisAlagbe\\CascadeProjects\\edgemail
npx wrangler dev --port 8787`}
              </pre>
              <p className="text-xs text-muted-foreground mt-3">
                Look for the line:{' '}
                <code className="font-mono">[wrangler:info] Ready on http://127.0.0.1:8787</code>,
                then refresh this page.
              </p>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                className="btn-primary"
                onClick={() => nav(0)}
              >
                <CoreIcon name="reload" size={14} />
                Retry
              </button>
              <Link to="/" className="btn-secondary">Back to landing</Link>
            </div>

            {status.detail && (
              <details className="mt-5">
                <summary className="cursor-pointer text-xs mono-label">Fetch detail</summary>
                <pre className="mt-2 text-[11px] bg-muted rounded-sm p-3 overflow-auto">
                  {status.detail}
                </pre>
              </details>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Pending() {
  return (
    <div role="status" aria-busy="true" className="h-full grid place-items-center p-8">
      <div className="text-center">
        <div className="mono-label mb-2">Loading</div>
        <p className="text-sm text-muted-foreground">Fetching your organization…</p>
      </div>
    </div>
  );
}

function Unauthorized({
  status,
}: {
  status: Extract<MeStatus, { state: 'unauthorized' }>;
}) {
  const { signOut } = useClerk();
  const nav = useNavigate();

  // Friendly headline for the common cases we recognize, fallback otherwise.
  const headline =
    status.error === 'invalid_token'
      ? 'We could not verify your session.'
      : 'You are not signed in.';

  return (
    <div role="alert" className="h-full overflow-y-auto p-8">
      <div className="card p-6 max-w-2xl mx-auto">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-sm bg-accent/10 border border-accent/30 grid place-items-center text-accent shrink-0">
            <CoreIcon name="warning" size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-normal tracking-sub leading-heading mb-1">
              {headline}
            </h2>
            {status.hint ? (
              <p className="text-sm text-foreground/80 leading-body">{status.hint}</p>
            ) : (
              <p className="text-sm text-muted-foreground leading-body">
                The Worker rejected the request. The most likely causes are below.
              </p>
            )}

            <ul className="mt-4 space-y-2 text-sm">
              <Item
                ok={false}
                title="Clerk secret key matches the publishable key"
                body={
                  <>Confirm <code className="font-mono">CLERK_SECRET_KEY</code> in <code className="font-mono">.dev.vars</code> starts with <code className="font-mono">sk_test_</code> and is from the same Clerk instance as the publishable key in <code className="font-mono">web/.env</code>. Restart the Worker after editing.</>
                }
              />
              <Item
                ok={false}
                title="Session token is fresh"
                body="Sign out and sign back in to mint a new token. Tokens expire after ~1 hour by default."
              />
              <Item
                ok={false}
                title="Worker is reachable"
                body={
                  <>Vite proxies <code className="font-mono">/api/*</code> to <code className="font-mono">http://localhost:8787</code>. Make sure <code className="font-mono">wrangler dev</code> is still running in the other terminal.</>
                }
              />
            </ul>

            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                className="btn-primary"
                onClick={async () => {
                  await signOut();
                  nav('/sign-in');
                }}
              >
                Sign out &amp; try again
              </button>
              <Link to="/" className="btn-secondary">Back to landing</Link>
            </div>

            {status.detail && (
              <details className="mt-5">
                <summary className="cursor-pointer text-xs mono-label">
                  Worker error detail
                </summary>
                <pre className="mt-2 text-[11px] bg-muted rounded-sm p-3 overflow-auto">
{`error:  ${status.error}
detail: ${status.detail}`}
                </pre>
              </details>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function NoOrg() {
  return (
    <div role="status" className="h-full overflow-y-auto p-8">
      <div className="card p-8 max-w-2xl mx-auto text-center">
        <div className="h-14 w-14 rounded-sm bg-accent/10 border border-accent/30 grid place-items-center text-accent mx-auto mb-5">
          <CoreIcon name="building" size={28} />
        </div>
        <div className="mono-label text-accent mb-2">Welcome to edgemail</div>
        <h2 className="text-3xl font-normal tracking-sub leading-heading mb-3">
          Create your first workspace.
        </h2>
        <p className="text-sm text-muted-foreground leading-body max-w-md mx-auto mb-6">
          An edgemail workspace is a Clerk organization. Everyone on your
          support team joins the same org; customer emails route to it.
        </p>
        <p className="text-sm mb-6">
          Use the <b>organization switcher</b> in the sidebar (the small chip
          near your avatar) and hit <b>&quot;Create organization&quot;</b>.
          That&apos;s it — the Worker auto-provisions your{' '}
          <code className="font-mono">&lt;slug&gt;@edgemail.cloud</code> address.
        </p>
        <div className="flex gap-2 justify-center">
          <a
            href="#clerk-org-switcher"
            className="btn-primary"
            onClick={(e) => {
              e.preventDefault();
              // Bring attention to the switcher in the sidebar.
              document
                .querySelector<HTMLElement>('[aria-label="Primary sidebar"] button')
                ?.click();
            }}
          >
            Create workspace
          </a>
        </div>
      </div>
    </div>
  );
}

function Item({ ok, title, body }: { ok: boolean; title: string; body: ReactNode }) {
  return (
    <li className="flex items-start gap-2.5">
      <CoreIcon
        name={ok ? 'check-alt' : 'circle'}
        size={14}
        className={ok ? 'text-success' : 'text-muted-foreground mt-0.5'}
      />
      <div>
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground leading-body">{body}</div>
      </div>
    </li>
  );
}
